/**
 * Суть игры в четырёх экранах при первом входе (§24.2).
 *
 * Назначение файла: один раз объяснить, что это за игра. Дальше игрок ведётся
 * подсказкой следующего шага, а не листалкой. Закрыть можно в любой момент;
 * вернуть — из настроек.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

/** Порядок экранов. Он же порядок обучения: улей, дом, ноги, земля. */
const SLIDES = ["hive", "hearth", "traces", "land"] as const;

export interface OnboardingSheetProps {
  readonly onClose: () => void;
}

export function OnboardingSheet({ onClose }: OnboardingSheetProps): React.JSX.Element {
  const { t } = useTranslation(["onboarding", "common"]);
  const [index, setIndex] = useState(0);

  const slide = SLIDES[index] ?? SLIDES[0];
  const isLast = index === SLIDES.length - 1;

  return (
    <div className="sheet onboarding" role="dialog" aria-modal="true">
      <header className="sheet__head">
        <div>
          <h2 className="sheet__title">{t("onboarding:intro.title")}</h2>
          <p className="onboarding__counter">
            {t("onboarding:intro.step", { current: index + 1, total: SLIDES.length })}
          </p>
        </div>
        <button type="button" className="button" onClick={onClose}>
          {t("onboarding:intro.skip")}
        </button>
      </header>

      <div className="onboarding__slide">
        <h3 className="onboarding__slide-title">{t(`onboarding:intro.${slide}.title`)}</h3>
        <p className="onboarding__slide-text">{t(`onboarding:intro.${slide}.text`)}</p>
      </div>

      <ol className="onboarding__dots" aria-hidden="true">
        {SLIDES.map((name, position) => (
          <li
            key={name}
            className={
              position === index ? "onboarding__dot onboarding__dot--on" : "onboarding__dot"
            }
          />
        ))}
      </ol>

      <div className="onboarding__actions">
        <button
          type="button"
          className="button"
          disabled={index === 0}
          onClick={() => setIndex((value) => Math.max(0, value - 1))}
        >
          {t("onboarding:intro.back")}
        </button>
        <button
          type="button"
          className="button button--primary"
          onClick={() => {
            if (isLast) {
              onClose();
              return;
            }
            setIndex((value) => Math.min(SLIDES.length - 1, value + 1));
          }}
        >
          {isLast ? t("onboarding:intro.start") : t("onboarding:intro.next")}
        </button>
      </div>
    </div>
  );
}
